import { Announcement, Badge, Button, Layout, LineChart } from '@utilities';

import ComparisonView from '../ComparisonView';
import { type TurfItem } from '../../utilities/adapter';
import { type ReachCurveRow, type TurfReachResult } from '../../utilities/algorithm';
import { type ComparisonDimension, type ComparisonKeyFinding } from '../../utilities/comparison';
import { formatFrequency, formatPercent } from '../../utilities/format';
import { TURF_BASKET_STATES, type TurfBasketStateValue } from '../../utilities/state';

import styles from '../../_index.module.scss';

interface ResultsPanelProps {
  basketItems: TurfItem[];
  basketState: TurfBasketStateValue;
  comparisonDimensions: ComparisonDimension[];
  completeRespondentCount: number;
  filteredRespondentCount: number;
  keyFindings: ComparisonKeyFinding[];
  onExportCsv: () => void;
  reach: TurfReachResult;
  reachCurveRows: ReachCurveRow[];
}

const ResultsPanel = ({
  basketItems,
  basketState,
  comparisonDimensions,
  completeRespondentCount,
  filteredRespondentCount,
  keyFindings,
  onExportCsv,
  reach,
  reachCurveRows,
}: ResultsPanelProps) => (
  <Layout.Flex.Column className={styles['turf-simulator-panel']} gap="large">
    <div className={styles['turf-simulator-panel-header']}>
      <div>
        <h3>Results</h3>
        <p>{filteredRespondentCount} filtered respondents</p>
      </div>
      <Button
        data-testid="turf-simulator-export-csv-button"
        isDisabled={!basketItems.length || filteredRespondentCount === 0}
        onClick={onExportCsv}
        text="Export CSV"
        variant="secondary"
      />
    </div>

    {basketState === TURF_BASKET_STATES.BEST_SET && (
      <Badge className={styles['turf-simulator-basket-badge']} label="Best set" variant="success" />
    )}

    {filteredRespondentCount === 0 && (
      <Announcement
        header="No respondents after filter"
        text={`The active filters exclude all ${completeRespondentCount} complete respondents.`}
        variant="warn"
      />
    )}

    <dl className={styles['turf-simulator-kpis']}>
      <div>
        <dt>Reach</dt>
        <dd>{formatPercent(reach.pct)}</dd>
        <span>{reach.hit} respondents</span>
      </div>
      <div>
        <dt>Frequency</dt>
        <dd>{formatFrequency(reach.frequency)}</dd>
        <span>items among reached</span>
      </div>
      <div>
        <dt>Basket / n</dt>
        <dd>
          {basketItems.length} / {filteredRespondentCount}
        </dd>
        <span>selected items</span>
      </div>
    </dl>

    {basketItems.length > 0 && <ComparisonView dimensions={[]} keyFindings={keyFindings} />}

    {basketItems.length === 0 ? (
      <Announcement
        header="Empty basket"
        text="Select items to calculate manual TURF reach."
        variant="info"
      />
    ) : (
      <>
        <div className={styles['turf-simulator-selected-items']}>
          <h4>Selected Items</h4>
          <ul>
            {basketItems.map((item) => (
              <li key={item.id}>{item.label}</li>
            ))}
          </ul>
        </div>
        <div className={styles['turf-simulator-curve']}>
          <LineChart
            chartTitle="Reach Curve"
            hasLegend={false}
            maxHeight={280}
            series={[
              {
                data: reachCurveRows.map((row) => [row.size, Math.round(row.pct * 1000) / 10]),
                name: 'Reach',
              },
            ]}
            showExportMenu={false}
            xAxis={{
              allowDecimals: false,
              title: { text: 'Basket size' },
            }}
            yAxis={{
              max: 100,
              min: 0,
              title: { text: 'Reach %' },
            }}
          />
        </div>
        <table aria-label="TURF reach curve" className={styles['turf-simulator-curve-table']}>
          <thead>
            <tr>
              <th>Size</th>
              <th>Item Added</th>
              <th>Reach %</th>
              <th>Reach Count</th>
              <th>Frequency</th>
              <th>Incremental Reach</th>
            </tr>
          </thead>
          <tbody>
            {reachCurveRows.map((row) => (
              <tr key={`${row.size}-${row.itemAddedId}`}>
                <td>{row.size}</td>
                <td>
                  {row.itemAddedLabel}
                  {row.itemAddedPinned && (
                    <span className={styles['turf-simulator-table-meta']}>Pinned</span>
                  )}
                </td>
                <td>{formatPercent(row.pct)}</td>
                <td>{row.hit}</td>
                <td>{formatFrequency(row.frequency)}</td>
                <td>{formatPercent(row.incrementalPct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <ComparisonView dimensions={comparisonDimensions} keyFindings={[]} />
      </>
    )}
  </Layout.Flex.Column>
);

export default ResultsPanel;
